"use client";

import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { AlertCircle, Loader2, Users, BookOpen, ChevronLeft, ChevronRight } from "lucide-react";
import type {
  DowngradePreview,
  DowngradePreviewBox,
} from "@/lib/downgrade-service";
import { initiateDowngrade } from "@/lib/downgrade-service";
import { useToast } from "@/hooks/use-toast";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preview: DowngradePreview;
  /** Called after a successful initiateDowngrade */
  onComplete: () => void;
}

type Step = "info" | "boxes" | "box-detail" | "confirm";

// ── Helpers ───────────────────────────────────────────────────────────────────

function overLimitBadge(count: number, noun: string) {
  if (count <= 0) return null;
  return (
    <Badge variant="destructive" className="text-xs">
      {count} {noun} over limit
    </Badge>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function DowngradeSelectionDialog({ open, onOpenChange, preview, onComplete }: Props) {
  const { toast } = useToast();
  const { newLimits } = preview;

  // How many boxes to keep active
  const maxBoxes = newLimits.maxRecipeBoxes ?? Infinity;
  const maxRecipes = newLimits.maxRecipesPerBox ?? Infinity;
  const maxMembers = newLimits.maxMembersPerBox ?? Infinity;

  // Step state
  const [step, setStep] = useState<Step>("info");
  const [detailBoxIndex, setDetailBoxIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Selection state
  const [activeBoxIds, setActiveBoxIds] = useState<Set<string>>(() => {
    // Pre-select oldest N boxes (same as Plan A default)
    const ids = preview.boxes.slice(0, Math.min(maxBoxes, preview.boxes.length)).map((b) => b.id);
    return new Set(ids);
  });

  // Per-box recipe/member selections: Map<boxId, Set<recipeId>> and Map<boxId, Set<uid>>
  const [activeRecipeIds, setActiveRecipeIds] = useState<Map<string, Set<string>>>(() => {
    const m = new Map<string, Set<string>>();
    for (const box of preview.boxes) {
      const ids = box.recipes
        .filter((r) => r.isActive)
        .slice(0, Math.min(maxRecipes, box.recipes.length))
        .map((r) => r.id);
      m.set(box.id, new Set(ids));
    }
    return m;
  });

  const [activeMemberUids, setActiveMemberUids] = useState<Map<string, Set<string>>>(() => {
    const m = new Map<string, Set<string>>();
    for (const box of preview.boxes) {
      const uids = box.members
        .filter((mem) => mem.isActive)
        .slice(0, Math.min(maxMembers, box.members.length))
        .map((mem) => mem.uid);
      m.set(box.id, new Set(uids));
    }
    return m;
  });

  // ── Derived validation ─────────────────────────────────────────────────────

  const selectedBoxCount = activeBoxIds.size;
  const boxesOverLimit = selectedBoxCount > maxBoxes;

  const activeBoxesNeedingDetail = preview.boxes.filter(
    (b) => activeBoxIds.has(b.id) && (b.recipesOverLimit > 0 || b.membersOverLimit > 0),
  );

  // Validation: per-box limits for currently active boxes
  const perBoxErrors = useMemo(() => {
    const errors: string[] = [];
    for (const box of preview.boxes) {
      if (!activeBoxIds.has(box.id)) continue;
      const selectedRecipes = activeRecipeIds.get(box.id)?.size ?? 0;
      const selectedMembers = activeMemberUids.get(box.id)?.size ?? 0;
      if (selectedRecipes > maxRecipes) {
        errors.push(`"${box.title}": ${selectedRecipes - maxRecipes} too many recipes selected`);
      }
      if (selectedMembers > maxMembers) {
        errors.push(`"${box.title}": ${selectedMembers - maxMembers} too many members selected`);
      }
    }
    return errors;
  }, [activeBoxIds, activeRecipeIds, activeMemberUids, preview.boxes, maxRecipes, maxMembers]);

  const canProceed = !boxesOverLimit && perBoxErrors.length === 0;

  // ── Toggle helpers ─────────────────────────────────────────────────────────

  function toggleBox(boxId: string, checked: boolean) {
    setActiveBoxIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(boxId);
      else next.delete(boxId);
      return next;
    });
  }

  function toggleRecipe(boxId: string, recipeId: string, checked: boolean) {
    setActiveRecipeIds((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(boxId) ?? []);
      if (checked) set.add(recipeId);
      else set.delete(recipeId);
      next.set(boxId, set);
      return next;
    });
  }

  function toggleMember(boxId: string, uid: string, checked: boolean) {
    setActiveMemberUids((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(boxId) ?? []);
      if (checked) set.add(uid);
      else set.delete(uid);
      next.set(boxId, set);
      return next;
    });
  }

  // ── Submit ─────────────────────────────────────────────────────────────────

  async function handleSubmit() {
    setSubmitting(true);
    try {
      // Collect all active recipe IDs and member UIDs across all active boxes
      const allActiveRecipeIds: string[] = [];
      const allActiveMemberUids: string[] = [];

      for (const [boxId, recipeSet] of activeRecipeIds) {
        if (activeBoxIds.has(boxId)) {
          allActiveRecipeIds.push(...recipeSet);
        }
      }
      for (const [boxId, memberSet] of activeMemberUids) {
        if (activeBoxIds.has(boxId)) {
          allActiveMemberUids.push(...memberSet);
        }
      }

      await initiateDowngrade({
        activeBoxIds: [...activeBoxIds],
        activeRecipeIds: allActiveRecipeIds,
        activeMemberUids: allActiveMemberUids,
      });

      toast({ title: "Downgrade complete", description: "Your account has been updated." });
      onComplete();
      onOpenChange(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Something went wrong";
      toast({ title: "Downgrade failed", description: msg, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  // ── Step: Info ─────────────────────────────────────────────────────────────

  function renderInfo() {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Confirm Downgrade to Free</DialogTitle>
          <DialogDescription>
            Review what will change when your account moves to the free tier.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 text-sm">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
            <div className="flex gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="space-y-1">
                <p className="font-medium">Items over your new limits will be deactivated</p>
                <p className="text-xs opacity-80">
                  Deactivated items are not deleted — they're hidden from collaborators until you
                  re-upgrade. You can swap which items are active after a 30-day lock period.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border p-4 space-y-2">
            <p className="font-medium">Your new free limits:</p>
            <ul className="space-y-1 text-muted-foreground">
              <li>
                Recipe Boxes:{" "}
                <span className="font-semibold text-foreground">
                  {newLimits.maxRecipeBoxes ?? "Unlimited"}
                </span>
              </li>
              <li>
                Recipes per Box:{" "}
                <span className="font-semibold text-foreground">
                  {newLimits.maxRecipesPerBox ?? "Unlimited"}
                </span>
              </li>
              <li>
                Members per Box:{" "}
                <span className="font-semibold text-foreground">
                  {newLimits.maxMembersPerBox ?? "Unlimited"}
                </span>
              </li>
            </ul>
          </div>

          {preview.mustDeactivateBoxes > 0 && (
            <p className="text-destructive font-medium">
              You currently have {preview.currentActiveBoxCount} active boxes — you need to
              deactivate at least {preview.mustDeactivateBoxes}.
            </p>
          )}

          {preview.boxes.some((b) => b.recipesOverLimit > 0 || b.membersOverLimit > 0) && (
            <p className="text-muted-foreground text-xs">
              Some boxes also have recipes or members over the new per-box limits. You'll choose
              which to keep on the next screens.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => setStep("boxes")}>
            Continue <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </DialogFooter>
      </>
    );
  }

  // ── Step: Box selection ────────────────────────────────────────────────────

  function renderBoxSelection() {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Select Active Recipe Boxes</DialogTitle>
          <DialogDescription>
            Choose up to {maxBoxes === Infinity ? "unlimited" : maxBoxes} boxes to keep active.
            Others will be deactivated (not deleted).
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-96 py-2">
          <div className="space-y-2 pr-3">
            {preview.boxes.map((box) => {
              const checked = activeBoxIds.has(box.id);
              return (
                <label
                  key={box.id}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                    checked ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                  }`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(val) => toggleBox(box.id, !!val)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{box.title}</p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <BookOpen className="h-3 w-3" /> {box.activeRecipeCount} recipes
                      </span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Users className="h-3 w-3" /> {box.activeMemberCount} members
                      </span>
                      {checked && overLimitBadge(box.recipesOverLimit, "recipes")}
                      {checked && overLimitBadge(box.membersOverLimit, "members")}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </ScrollArea>

        {boxesOverLimit && (
          <p className="text-sm text-destructive font-medium">
            You have {selectedBoxCount} selected — maximum is {maxBoxes}. Deselect{" "}
            {selectedBoxCount - maxBoxes} more.
          </p>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setStep("info")}>
            <ChevronLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <Button
            disabled={boxesOverLimit}
            onClick={() => {
              if (activeBoxesNeedingDetail.length > 0) {
                setDetailBoxIndex(0);
                setStep("box-detail");
              } else {
                setStep("confirm");
              }
            }}
          >
            Continue <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </DialogFooter>
      </>
    );
  }

  // ── Step: Per-box recipe/member detail ────────────────────────────────────

  function renderBoxDetail() {
    const box: DowngradePreviewBox = activeBoxesNeedingDetail[detailBoxIndex];
    if (!box) return null;

    const boxRecipeIds = activeRecipeIds.get(box.id) ?? new Set<string>();
    const boxMemberUids = activeMemberUids.get(box.id) ?? new Set<string>();
    const recipeCount = boxRecipeIds.size;
    const memberCount = boxMemberUids.size;
    const recipeOver = recipeCount > maxRecipes;
    const memberOver = memberCount > maxMembers;

    const isLast = detailBoxIndex >= activeBoxesNeedingDetail.length - 1;

    return (
      <>
        <DialogHeader>
          <DialogTitle className="truncate">Trim "{box.title}"</DialogTitle>
          <DialogDescription>
            Box {detailBoxIndex + 1} of {activeBoxesNeedingDetail.length} — select which items to
            keep active.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-80 py-2 space-y-4">
          <div className="pr-3 space-y-4">
            {/* Recipes */}
            {box.recipesOverLimit > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">Recipes</p>
                  <span className={`text-xs ${recipeOver ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
                    {recipeCount} / {maxRecipes === Infinity ? "∞" : maxRecipes} selected
                  </span>
                </div>
                <div className="space-y-1.5">
                  {box.recipes.map((recipe) => {
                    const checked = boxRecipeIds.has(recipe.id);
                    return (
                      <label
                        key={recipe.id}
                        className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                          checked ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                        }`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(val) => toggleRecipe(box.id, recipe.id, !!val)}
                        />
                        <span className="flex-1 truncate">{recipe.title}</span>
                      </label>
                    );
                  })}
                </div>
                {recipeOver && (
                  <p className="mt-1 text-xs text-destructive">
                    Deselect {recipeCount - maxRecipes} more recipe(s).
                  </p>
                )}
              </div>
            )}

            {box.recipesOverLimit > 0 && box.membersOverLimit > 0 && (
              <Separator />
            )}

            {/* Members */}
            {box.membersOverLimit > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">Members</p>
                  <span className={`text-xs ${memberOver ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
                    {memberCount} / {maxMembers === Infinity ? "∞" : maxMembers} selected
                  </span>
                </div>
                <div className="space-y-1.5">
                  {box.members.map((member) => {
                    const checked = boxMemberUids.has(member.uid);
                    return (
                      <label
                        key={member.uid}
                        className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                          checked ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                        }`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(val) => toggleMember(box.id, member.uid, !!val)}
                        />
                        <span className="flex-1 truncate">
                          {member.displayName ?? member.email ?? member.uid}
                        </span>
                        <Badge variant="outline" className="text-xs shrink-0">
                          {member.role}
                        </Badge>
                      </label>
                    );
                  })}
                </div>
                {memberOver && (
                  <p className="mt-1 text-xs text-destructive">
                    Deselect {memberCount - maxMembers} more member(s).
                  </p>
                )}
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => {
              if (detailBoxIndex === 0) setStep("boxes");
              else setDetailBoxIndex((i) => i - 1);
            }}
          >
            <ChevronLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <Button
            disabled={recipeOver || memberOver}
            onClick={() => {
              if (isLast) setStep("confirm");
              else setDetailBoxIndex((i) => i + 1);
            }}
          >
            {isLast ? "Review" : "Next Box"} <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </DialogFooter>
      </>
    );
  }

  // ── Step: Confirm ─────────────────────────────────────────────────────────

  function renderConfirm() {
    const willDeactivateBoxCount = preview.boxes.length - activeBoxIds.size;

    return (
      <>
        <DialogHeader>
          <DialogTitle>Confirm Your Selection</DialogTitle>
          <DialogDescription>
            Review your choices before completing the downgrade.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4 text-sm">
          <div className="rounded-lg border p-4 space-y-2">
            <p>
              <span className="font-medium">{activeBoxIds.size}</span> boxes will remain active
              {willDeactivateBoxCount > 0 && (
                <>, <span className="font-medium text-muted-foreground">{willDeactivateBoxCount}</span> will be deactivated</>
              )}
            </p>
            {activeBoxesNeedingDetail.map((box) => {
              const recipeCount = activeRecipeIds.get(box.id)?.size ?? 0;
              const memberCount = activeMemberUids.get(box.id)?.size ?? 0;
              return (
                <div key={box.id} className="border-t pt-2 text-muted-foreground text-xs">
                  <p className="font-semibold text-foreground truncate">{box.title}</p>
                  <p>{recipeCount} active recipes · {memberCount} active members</p>
                </div>
              );
            })}
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900 text-xs dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
            After downgrading, you can swap active items after a <strong>30-day</strong> waiting
            period.
          </div>

          {perBoxErrors.length > 0 && (
            <div className="text-destructive text-xs space-y-0.5">
              {perBoxErrors.map((e) => <p key={e}>{e}</p>)}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => {
              if (activeBoxesNeedingDetail.length > 0) {
                setDetailBoxIndex(activeBoxesNeedingDetail.length - 1);
                setStep("box-detail");
              } else {
                setStep("boxes");
              }
            }}
          >
            <ChevronLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <Button
            variant="destructive"
            disabled={!canProceed || submitting}
            onClick={handleSubmit}
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirm Downgrade
          </Button>
        </DialogFooter>
      </>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {step === "info" && renderInfo()}
        {step === "boxes" && renderBoxSelection()}
        {step === "box-detail" && renderBoxDetail()}
        {step === "confirm" && renderConfirm()}
      </DialogContent>
    </Dialog>
  );
}
